// El Laboratorio de IA sobre TanStack Query. Las mutaciones invalidan las
// claves que tocan en vez de parchear la caché a mano.

import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { fetchInformesEnProceso } from "@/lib/informes";
import { fetchMaestroItems } from "@/lib/plantillas";
import {
  borrarCasoRequest,
  calificarRequest,
  crearCasoRequest,
  crearExperimentoRequest,
  fetchCasos,
  fetchCatalogoDePrompts,
  fetchCorrida,
  fetchCorridas,
  fetchExperimento,
  fetchExperimentos,
  fetchLlamada,
  fetchResumenDelMes,
  fetchRevisionDelPrompt,
  fetchRevisionesDelPrompt,
  guardarBorradorRequest,
  publicarPromptRequest,
  volverACorrerRequest,
  type Calificacion,
  type ExperimentoCompleto,
  type FiltrosDeCorridas,
  type NuevoCaso,
  type NuevoExperimento,
} from "@/lib/laboratorio";

export const PROMPTS_KEY = ["laboratorio", "prompts"] as const;
export const CASOS_KEY = ["laboratorio", "casos"] as const;
export const EXPERIMENTOS_KEY = ["laboratorio", "experimentos"] as const;
export const experimentoKey = (id: number) => ["laboratorio", "experimento", id] as const;

/** Mientras un experimento corre, se vuelve a leer cada ~3 s. */
export const INTERVALO_DEL_EXPERIMENTO = 3000;

export function usePromptsDelLaboratorio() {
  return useQuery({ queryKey: PROMPTS_KEY, queryFn: fetchCatalogoDePrompts });
}

export function useResumenDelMes() {
  return useQuery({ queryKey: ["laboratorio", "resumen"], queryFn: fetchResumenDelMes });
}

export function useRevisionesDelPrompt(funcionId: number | null) {
  return useQuery({
    queryKey: ["laboratorio", "revisiones", funcionId],
    queryFn: () => fetchRevisionesDelPrompt(funcionId!),
    enabled: funcionId !== null,
  });
}

export function useCuerpoDeRevision(funcionId: number | null, revisionId: number | null) {
  return useQuery({
    queryKey: ["laboratorio", "revision", funcionId, revisionId],
    queryFn: () => fetchRevisionDelPrompt(funcionId!, revisionId!),
    enabled: funcionId !== null && revisionId !== null,
  });
}

export function useGuardarBorrador() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ funcionId, texto }: { funcionId: number; texto: string }) =>
      guardarBorradorRequest(funcionId, texto),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: PROMPTS_KEY });
      void cliente.invalidateQueries({ queryKey: ["laboratorio", "revisiones"] });
    },
  });
}

export function usePublicarPrompt() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (funcionId: number) => publicarPromptRequest(funcionId),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ["laboratorio"] });
      void cliente.invalidateQueries({ queryKey: ["ia", "funciones"] });
    },
  });
}

export function useCasos() {
  return useQuery({ queryKey: CASOS_KEY, queryFn: fetchCasos });
}

export function useCrearCaso() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (input: NuevoCaso) => crearCasoRequest(input),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CASOS_KEY }),
  });
}

export function useBorrarCaso() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => borrarCasoRequest(id),
    onSuccess: () => cliente.invalidateQueries({ queryKey: CASOS_KEY }),
  });
}

export function useExperimentos(funcionId?: number) {
  return useQuery({
    queryKey: [...EXPERIMENTOS_KEY, funcionId ?? "todas"],
    queryFn: () => fetchExperimentos(funcionId),
  });
}

/** Sondea mientras el experimento corre y se apaga solo al terminar. */
export function useExperimento(id: number | null) {
  return useQuery({
    queryKey: experimentoKey(id ?? 0),
    queryFn: () => fetchExperimento(id!),
    enabled: id !== null,
    refetchInterval: (consulta) =>
      (consulta.state.data as ExperimentoCompleto | undefined)?.estado === "corriendo"
        ? INTERVALO_DEL_EXPERIMENTO
        : false,
  });
}

export function useCrearExperimento() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (input: NuevoExperimento) => crearExperimentoRequest(input),
    onSuccess: (experimento) => {
      cliente.setQueryData(experimentoKey(experimento.id), experimento);
      void cliente.invalidateQueries({ queryKey: EXPERIMENTOS_KEY });
    },
  });
}

export function useVolverACorrer() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => volverACorrerRequest(id),
    onSuccess: (experimento) => {
      cliente.setQueryData(experimentoKey(experimento.id), experimento);
      void cliente.invalidateQueries({ queryKey: EXPERIMENTOS_KEY });
    },
  });
}

export function useCalificar(experimentoId: number) {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({
      resultadoId,
      calificacion,
      comentario,
    }: {
      resultadoId: number;
      calificacion: Calificacion;
      comentario: string | null;
    }) => calificarRequest(experimentoId, resultadoId, calificacion, comentario),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: experimentoKey(experimentoId) });
      void cliente.invalidateQueries({ queryKey: EXPERIMENTOS_KEY });
    },
  });
}

export function useCorridas(filtros: FiltrosDeCorridas) {
  return useInfiniteQuery({
    queryKey: ["laboratorio", "corridas", filtros],
    queryFn: ({ pageParam }) => fetchCorridas(filtros, pageParam),
    initialPageParam: 1,
    getNextPageParam: (ultima) => (ultima.meta.pagina < ultima.meta.ultima_pagina ? ultima.meta.pagina + 1 : undefined),
  });
}

/** El total de corridas sin filtros, para el contador de la pestaña. */
export function useTotalDeCorridas() {
  return useQuery({
    queryKey: ["laboratorio", "corridas-total"],
    queryFn: () => fetchCorridas({ informe: "", funcion: "", revision: "", estado: "cualquiera" }),
    select: (pagina) => pagina.meta.total,
  });
}

export function useCorrida(id: number | null) {
  return useQuery({
    queryKey: ["laboratorio", "corrida", id],
    queryFn: () => fetchCorrida(id!),
    enabled: id !== null,
  });
}

export function useLlamada(id: number | null) {
  return useQuery({
    queryKey: ["laboratorio", "llamada", id],
    queryFn: () => fetchLlamada(id!),
    enabled: id !== null,
  });
}

/** Los informes que se pueden congelar como caso o filtrar en el registro (primera página del listado). */
export function useInformesDelLaboratorio() {
  return useQuery({
    queryKey: ["laboratorio", "informes"],
    queryFn: () => fetchInformesEnProceso(),
    select: (pagina) => pagina.informes,
    staleTime: 60_000,
  });
}

/** Las secciones del maestro que redacta la IA: con cuáles se prueba un prompt. */
export function useSeccionesRedactables() {
  return useQuery({
    queryKey: ["laboratorio", "secciones-redactables"],
    queryFn: fetchMaestroItems,
    select: (items) =>
      items
        .filter((i) => i.codigo && i.modo_produccion === "redactado_ia")
        .map((i) => ({ codigo: i.codigo as string, nombre: i.nombre })),
    staleTime: 5 * 60_000,
  });
}
